import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../lib/api-context';

const STORAGE_KEY = 'bluebird.tabs.v2';
const LEGACY_STORAGE_KEY = 'bluebird.sessions.v1';

/**
 * Persisted-tab union. The tab strip surfaces both kinds; the active
 * pane is resolved by `kind` discriminator (chat vs file viewer).
 *
 *  - `chat` tabs map 1:1 to a Bluebird session (`sessionId === id`).
 *  - `file` tabs render a viewer for a file in the unified workspace
 *    plane. The two `scope` values name two VIEWS into the same R2
 *    namespace, not two separate stores: `'persistent'` lists the
 *    workspace root (everything under `/workspace/`, including the
 *    `sessions/` subtree), `'workspace'` lists one session's subtree
 *    (`/workspace/sessions/<sid>/`). Tab id encodes scope + path so
 *    "open the same file twice" focuses the existing tab instead of
 *    spawning a duplicate. The two-name scheme is preserved for
 *    URL/localStorage backward compatibility.
 */
export type Tab = ChatTab | FileTab;

export interface ChatTab {
  kind: 'chat';
  /** sessionId, also used as `?session=` URL param. */
  id: string;
  /** Tab strip + sidebar title. Defaults to "New chat". */
  title: string;
  /** Epoch ms — last user input or stream activity. */
  lastActivity: number;
}

export interface FileTab {
  kind: 'file';
  /** `file:persistent:/path` or `file:workspace:<sessionId>:/path`. */
  id: string;
  /** Display title — basename + a small scope marker. */
  title: string;
  /**
   * View into the unified workspace plane:
   *  - `'persistent'` — workspace root (every file under `/workspace/`,
   *    including the `sessions/` subtree).
   *  - `'workspace'` — a single session's subtree
   *    (`/workspace/sessions/<sessionId>/`); requires `sessionId`.
   * Names preserved for URL / localStorage compatibility; semantics
   * are now two views into ONE plane (DD-CFS-25), not two planes.
   */
  scope: 'persistent' | 'workspace';
  /** Path relative to the scope's root, no leading slash. */
  path: string;
  /** Required when `scope === 'workspace'`. */
  sessionId?: string;
  /** Epoch ms — last time the user opened/refreshed this viewer. */
  lastActivity: number;
}

/** Back-compat alias so older callers don't break mid-refactor. */
export type SessionTab = ChatTab;

interface PersistedV2 {
  v: 2;
  tabs: Tab[];
}

interface LegacyV1Tab {
  id: string;
  title: string;
  lastActivity: number;
}
interface PersistedV1 {
  v: 1;
  tabs: LegacyV1Tab[];
}

function load(): Tab[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistedV2 | unknown;
      if (
        parsed &&
        typeof parsed === 'object' &&
        (parsed as PersistedV2).v === 2 &&
        Array.isArray((parsed as PersistedV2).tabs)
      ) {
        return (parsed as PersistedV2).tabs.filter(isValidTab);
      }
    }
    // v1 migration — old single-kind tab list.
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as PersistedV1 | unknown;
      if (
        parsed &&
        typeof parsed === 'object' &&
        (parsed as PersistedV1).v === 1 &&
        Array.isArray((parsed as PersistedV1).tabs)
      ) {
        return (parsed as PersistedV1).tabs
          .filter(
            (t): t is LegacyV1Tab =>
              typeof t.id === 'string' &&
              typeof t.title === 'string' &&
              typeof t.lastActivity === 'number',
          )
          .map<ChatTab>((t) => ({
            kind: 'chat',
            id: t.id,
            title: t.title,
            lastActivity: t.lastActivity,
          }));
      }
    }
    return [];
  } catch {
    return [];
  }
}

function isValidTab(t: unknown): t is Tab {
  if (!t || typeof t !== 'object') return false;
  const obj = t as Record<string, unknown>;
  if (obj.kind === 'chat') {
    return (
      typeof obj.id === 'string' &&
      typeof obj.title === 'string' &&
      typeof obj.lastActivity === 'number'
    );
  }
  if (obj.kind === 'file') {
    return (
      typeof obj.id === 'string' &&
      typeof obj.title === 'string' &&
      (obj.scope === 'persistent' || obj.scope === 'workspace') &&
      typeof obj.path === 'string' &&
      typeof obj.lastActivity === 'number' &&
      (obj.sessionId === undefined || typeof obj.sessionId === 'string')
    );
  }
  return false;
}

function save(tabs: Tab[]): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: PersistedV2 = { v: 2, tabs };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage quota / disabled — best-effort persistence.
  }
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `s-${crypto.randomUUID().slice(0, 12)}`;
  }
  return `s-${Math.random().toString(36).slice(2, 14)}`;
}

/**
 * Build a deterministic tab id for a file viewer so opening the same
 * file twice focuses the existing tab.
 */
export function fileTabId(
  scope: 'persistent' | 'workspace',
  path: string,
  sessionId?: string,
): string {
  const normalised = path.startsWith('/') ? path : `/${path}`;
  if (scope === 'workspace') {
    return `file:workspace:${sessionId ?? '_'}:${normalised}`;
  }
  return `file:persistent:${normalised}`;
}

function basename(path: string): string {
  const trimmed = path.replace(/\/+$/g, '');
  const slash = trimmed.lastIndexOf('/');
  return slash === -1 ? trimmed : trimmed.slice(slash + 1);
}

interface OpenFileTabInput {
  scope: 'persistent' | 'workspace';
  path: string;
  sessionId?: string;
}

interface CreateChatTabInput {
  /** Override the auto-generated id (used by sidebar to attach an existing session). */
  id?: string;
  title?: string;
}

interface SessionsApi {
  tabs: Tab[];
  createTab: (input?: CreateChatTabInput) => ChatTab;
  openFileTab: (input: OpenFileTabInput) => FileTab;
  removeTab: (id: string) => void;
  renameTab: (id: string, title: string) => void;
  touchTab: (id: string) => void;
  /** Update the title of a chat tab from a server-pushed event. No-op for unknown ids / file tabs. */
  applyServerSessionUpdate: (update: { id: string; title: string }) => void;
  /**
   * Currently focused tab id, or `null` when the workspace has no
   * tabs at all. Mirrored to the URL `?session=...` param, but reads
   * from local state — `useSearchParams` lags by one render under
   * react-router-dom v7's `BrowserRouter`, which wraps location
   * updates in `startTransition`.
   */
  activeId: string | null;
  /**
   * Atomically focus a tab: updates `activeId` immediately (urgent
   * priority) AND writes the URL (low-priority transition). The URL
   * eventually catches up, but the UI flips on the next render so
   * the user never sees a stale highlight.
   *
   * `replace: true` swaps the current history entry instead of
   * pushing a new one — used for auto-create / drift correction so
   * the back button doesn't trap on transient states.
   */
  focusTab: (id: string, options?: { replace?: boolean }) => void;
  /**
   * Fork a conversation at a specific message — DD-BLB-24. Calls the
   * server-side `forkSession` RPC, then materializes the new session
   * as a chat tab and focuses it. Resolves with the new tab once the
   * server responds; rejects with `ApiError` on backend failure (the
   * caller surfaces the error in the UI). Local state is unchanged
   * until the server confirms — no optimistic write — so a server-
   * side rejection doesn't leave an orphan tab pointing at a
   * non-existent session.
   */
  forkChat: (
    sourceSessionId: string,
    atMessageId: string,
    options?: { name?: string },
  ) => Promise<ChatTab>;
}

const SessionsContext = createContext<SessionsApi | null>(null);

/**
 * Wraps the app so any pane can read/mutate the canonical tab list
 * without prop drilling. Mounted in `App.tsx` above `<ChatWorkspace>`.
 */
export function TabsProvider({ children }: { children: ReactNode }) {
  const api = useSessionsInternal();
  return (
    <SessionsContext.Provider value={api}>{children}</SessionsContext.Provider>
  );
}

/**
 * Read the canonical tab list from a Provider. Throws if used outside
 * `<TabsProvider>` so misuse is loud.
 */
export function useSessions(): SessionsApi {
  const ctx = useContext(SessionsContext);
  if (!ctx) {
    throw new Error('useSessions must be used inside <TabsProvider>');
  }
  return ctx;
}

function useSessionsInternal(): SessionsApi {
  const [tabs, setTabs] = useState<Tab[]>(load);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSession = searchParams.get('session');
  const api = useApi();

  // Source of truth for the focused tab. Initialized from the URL on
  // mount, then driven by user actions (which also stamp the URL).
  // Living here means BOTH `ChatWorkspace` (the tab strip) and
  // `LeftSidebar` (the sessions list) can flip the active tab without
  // either having to wait on the other's URL transition.
  const [activeId, setActiveIdState] = useState<string | null>(() => {
    const initial = load();
    if (urlSession && initial.some((t) => t.id === urlSession)) {
      return urlSession;
    }
    return initial[0]?.id ?? null;
  });

  // Local-write guard: when `focusTab()` (or any other local writer)
  // calls `setSearchParams()`, react-router-dom v7 wraps the URL
  // update in `startTransition`, so `urlSession` lags by one (or
  // more) renders. During that window the URL→state effect would
  // otherwise see the stale URL, decide it's valid, and overwrite
  // the just-clicked `activeId`.
  //
  // We snapshot the URL value at the moment of the local write
  // (`staleUrl`) and skip URL→state only while `urlSession` still
  // equals that snapshot. As soon as `urlSession` changes to ANY
  // other value — the target landing, an external back/forward, or a
  // superseding navigation — we clear the guard and process the new
  // URL normally. This keeps the guard from getting sticky when a
  // local transition is interrupted before it commits.
  const pendingLocalRef = useRef<{
    target: string;
    staleUrl: string | null;
  } | null>(null);

  useEffect(() => {
    save(tabs);
  }, [tabs]);

  // Cross-browser-tab sync — when another browser tab edits the list,
  // mirror the new state into this one so the UI stays consistent.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setTabs(load());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // URL → state: pick up external URL changes (back/forward, paste,
  // an external link). The user-action path (focusTab) updates state
  // directly so the deferred URL transition firing here is a no-op.
  //
  // While a local write is in flight, `urlSession` still equals the
  // snapshot we captured at the time of the write — skip in that
  // case so we don't overwrite `activeId` back to the old URL. The
  // very next change of `urlSession` (target landing OR external
  // supersede) clears the guard and falls through to normal handling
  // so external navigation can never be ignored indefinitely.
  useEffect(() => {
    if (!urlSession) return;
    const pending = pendingLocalRef.current;
    if (pending !== null && urlSession === pending.staleUrl) return;
    if (pending !== null) pendingLocalRef.current = null;
    if (urlSession === activeId) return;
    if (!tabs.some((t) => t.id === urlSession)) return;
    setActiveIdState(urlSession);
  }, [urlSession, activeId, tabs]);

  // Drift-correct: if `activeId` points at a tab that's no longer in
  // the list (e.g., the user closed the active tab, or another browser
  // tab removed it via the storage event), fall back to the first
  // remaining tab.
  useEffect(() => {
    if (activeId && !tabs.some((t) => t.id === activeId)) {
      const fallback = tabs[0]?.id ?? null;
      setActiveIdState(fallback);
      if (fallback && fallback !== urlSession) {
        pendingLocalRef.current = { target: fallback, staleUrl: urlSession };
        setSearchParams({ session: fallback }, { replace: true });
      } else if (!fallback) {
        pendingLocalRef.current = null;
      }
    }
  }, [activeId, tabs, urlSession, setSearchParams]);

  // State → URL: stamp the URL when `activeId` and `urlSession` drift
  // apart from any path that updated state without going through
  // `focusTab` (e.g., the drift-correct effect above).
  //
  // Skip when `urlSession` already points at a valid tab — that means
  // the URL was changed by back/forward, paste, or an external link,
  // and the URL→state effect above will catch `activeId` up to the
  // URL on the next render. Writing here would clobber the user's
  // navigation by replacing the new URL with the stale `activeId`
  // before the URL→state effect's `setActiveIdState` commits.
  useEffect(() => {
    if (!activeId) return;
    if (urlSession === activeId) return;
    if (urlSession && tabs.some((t) => t.id === urlSession)) return;
    pendingLocalRef.current = { target: activeId, staleUrl: urlSession };
    setSearchParams({ session: activeId }, { replace: true });
  }, [activeId, urlSession, tabs, setSearchParams]);

  const focusTab = useCallback(
    (id: string, options?: { replace?: boolean }) => {
      // Skip the guard when the URL is already on `id` — there's no
      // transition in flight to wait out, so we don't want to ignore
      // external changes that may follow.
      if (id !== urlSession) {
        pendingLocalRef.current = { target: id, staleUrl: urlSession };
      }
      setActiveIdState(id);
      setSearchParams(
        { session: id },
        options?.replace ? { replace: true } : undefined,
      );
    },
    [urlSession, setSearchParams],
  );

  const createTab = useCallback(
    (input?: CreateChatTabInput): ChatTab => {
      const id = input?.id ?? newSessionId();
      const next: ChatTab = {
        kind: 'chat',
        id,
        title: input?.title?.trim() || 'New chat',
        lastActivity: Date.now(),
      };
      setTabs((prev) => {
        // De-dupe: clicking a session in the sidebar that already has
        // a tab should focus, not duplicate. Caller handles "focus"
        // separately via the URL.
        if (prev.some((t) => t.id === id)) return prev;
        return [...prev, next];
      });
      return next;
    },
    [],
  );

  const openFileTab = useCallback((input: OpenFileTabInput): FileTab => {
    const id = fileTabId(input.scope, input.path, input.sessionId);
    const titleBase = basename(input.path) || '/';
    const title = input.scope === 'workspace' ? `${titleBase} ·ws` : titleBase;
    const next: FileTab = {
      kind: 'file',
      id,
      title,
      scope: input.scope,
      path: input.path.replace(/^\/+/, ''),
      sessionId: input.sessionId,
      lastActivity: Date.now(),
    };
    setTabs((prev) => {
      const existing = prev.find((t) => t.id === id);
      if (existing) {
        return prev.map((t) => (t.id === id ? { ...t, lastActivity: Date.now() } : t));
      }
      return [...prev, next];
    });
    return next;
  }, []);

  const removeTab = useCallback((id: string): void => {
    setTabs((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const renameTab = useCallback((id: string, title: string): void => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, title: title.trim() || t.title } : t,
      ),
    );
  }, []);

  const touchTab = useCallback((id: string): void => {
    setTabs((prev) =>
      prev.map((t) => (t.id === id ? { ...t, lastActivity: Date.now() } : t)),
    );
  }, []);

  const applyServerSessionUpdate = useCallback(
    ({ id, title }: { id: string; title: string }): void => {
      const trimmed = title.trim();
      if (!trimmed) return;
      setTabs((prev) =>
        prev.map((t) => {
          if (t.kind !== 'chat' || t.id !== id) return t;
          if (t.title === trimmed) return t;
          return { ...t, title: trimmed };
        }),
      );
    },
    [],
  );

  const forkChat = useCallback(
    async (
      sourceSessionId: string,
      atMessageId: string,
      options?: { name?: string },
    ): Promise<ChatTab> => {
      // Server-side fork: copies messages 0..atMessageId into a new
      // sessionId, returns its metadata. We materialize a new chat tab
      // ONLY on success so an API failure doesn't leave a dangling tab.
      const meta = await api.forkSession(
        sourceSessionId,
        atMessageId,
        options?.name,
      );
      const next: ChatTab = {
        kind: 'chat',
        id: meta.id,
        title: meta.name && meta.name.length > 0 ? meta.name : 'Fork',
        lastActivity: Date.now(),
      };
      // Insert next to the source tab so the lineage reads naturally
      // in the tab strip; fall back to appending if the source isn't a
      // chat tab (shouldn't happen — UI gates on chat) or is missing.
      setTabs((prev) => {
        if (prev.some((t) => t.id === next.id)) return prev;
        const idx = prev.findIndex(
          (t) => t.kind === 'chat' && t.id === sourceSessionId,
        );
        if (idx < 0) return [...prev, next];
        return [...prev.slice(0, idx + 1), next, ...prev.slice(idx + 1)];
      });
      // Focus immediately so the user lands in the fork.
      focusTab(next.id);
      return next;
    },
    [api, focusTab],
  );

  return useMemo(
    () => ({
      tabs,
      createTab,
      openFileTab,
      removeTab,
      renameTab,
      touchTab,
      applyServerSessionUpdate,
      activeId,
      focusTab,
      forkChat,
    }),
    [
      tabs,
      createTab,
      openFileTab,
      removeTab,
      renameTab,
      touchTab,
      applyServerSessionUpdate,
      activeId,
      focusTab,
      forkChat,
    ],
  );
}
